import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import {
  CheckoutBlockedError,
  CheckoutEligibilityError,
  CheckoutEligibilityErrorCode,
} from '@pncl/common-models';
import { isCheckoutEligibilityError } from 'src/app/models/checkout-eligibility';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { NotificationToasterService } from 'src/app/services/notification-toaster.service';
import { PaymentService } from 'src/app/services/payment.service';
import {
  ModalTemplateComponent,
  PencilModalSizeEnum,
} from 'src/app/standalones/components/modals/modal-template/modal-template.component';
import {
  PencilButtonComponent,
  PencilButtonThemeEnum,
} from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';

export interface CheckoutBlockedModalData {
  error: CheckoutEligibilityError;
}

export enum CheckoutBlockedModalCloseResult {
  CLOSED = 'closed',
  UNBLOCKED = 'unblocked',
}

@Component({
  selector: 'app-checkout-blocked-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, ModalTemplateComponent, PencilButtonComponent],
  templateUrl: './checkout-blocked-modal.component.html',
  styleUrls: ['./checkout-blocked-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutBlockedModalComponent {
  protected readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  protected readonly IconTypes = IconTypes;
  protected readonly PencilModalSizeEnum = PencilModalSizeEnum;
  protected readonly CheckoutEligibilityErrorCode = CheckoutEligibilityErrorCode;

  protected isBusy = false;
  protected currentError: CheckoutEligibilityError;

  constructor(
    private readonly dialogRef: MatDialogRef<
      CheckoutBlockedModalComponent,
      CheckoutBlockedModalCloseResult
    >,
    @Inject(MAT_DIALOG_DATA) public readonly data: CheckoutBlockedModalData,
    private readonly customerSupportService: CustomerSupportService,
    private readonly paymentService: PaymentService,
    private readonly notificationToasterService: NotificationToasterService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.currentError = data.error;
  }

  get isBlocked(): boolean {
    return (
      this.currentError.error_code === CheckoutEligibilityErrorCode.CHECKOUT_BLOCKED_UNPAID_INVOICES
    );
  }

  get isVerificationFailed(): boolean {
    return (
      this.currentError.error_code ===
      CheckoutEligibilityErrorCode.CHECKOUT_BILLING_VERIFICATION_FAILED
    );
  }

  get message(): string {
    return this.currentError.err;
  }

  get formattedBalance(): string | null {
    if (!this.isBlocked) {
      return null;
    }
    const blocked = this.currentError as CheckoutBlockedError;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: blocked.currency || 'USD',
    }).format(blocked.outstandingAmount / 100);
  }

  contactSupport(): void {
    const invoiceHint = this.isBlocked
      ? ` Invoice IDs: ${(this.currentError as CheckoutBlockedError).outstandingInvoiceIds.join(
          ', ',
        )}.`
      : '';
    this.customerSupportService.openSupport({
      showCommand: 'showNewMessage',
      target: `I need help settling outstanding invoices so I can continue with checkout.${invoiceHint}`,
    });
  }

  reviewInvoices(): Promise<void> {
    // The popup must be opened synchronously in the click handler to avoid popup blockers.
    this.paymentService.openBufferWindow('Review invoices session');
    return this.recheckEligibility(() => this.paymentService.handlePortalSessionForInvoiceReview());
  }

  tryAgain(): Promise<void> {
    return this.recheckEligibility();
  }

  private async recheckEligibility(beforeCheck?: () => Promise<boolean>): Promise<void> {
    this.setBusy(true);
    try {
      if (beforeCheck && !(await beforeCheck())) {
        return;
      }
      await firstValueFrom(this.paymentService.checkCheckoutEligibility());
      this.notificationToasterService.showDefaultSuccessNotification(
        this.translateService.instant(
          'Your billing status looks clear. You can continue checkout.',
        ),
        this.notificationToasterService.generateNotificationCode(),
      );
      this.dialogRef.close(CheckoutBlockedModalCloseResult.UNBLOCKED);
    } catch (error: unknown) {
      const body = (error as { error?: unknown })?.error;
      if (isCheckoutEligibilityError(body)) {
        this.currentError = body;
      } else {
        this.notificationToasterService.showDefaultErrorNotificationV2(
          this.translateService.instant('Unable to verify billing'),
          this.translateService.instant(
            "We couldn't verify your institution's billing status. Please try again shortly.",
          ),
          this.notificationToasterService.generateNotificationCode(),
        );
      }
    } finally {
      this.setBusy(false);
    }
  }

  private setBusy(isBusy: boolean): void {
    this.isBusy = isBusy;
    this.cdr.markForCheck();
  }

  close(): void {
    this.dialogRef.close(CheckoutBlockedModalCloseResult.CLOSED);
  }
}
